import { Map, List } from 'immutable'
import {
  CONTENT_POLICY_OPTION_SUB_AI_ONLY,
  CONTENT_POLICY_OPTION_SUB_MONTHLY,
  CONTENT_POLICY_OPTION_SUB_ANNUAL,
  CONTENT_POLICY_OPTION_SUB_LIVE_ACCESS,
  CONTENT_POLICY_OPTION_ALACARTE,
} from '../content-policy/option-ids'
import {
  SECTION_TYPE_CONTENT_LIST,
  SECTION_TYPE_CONTENT_REFERENCES_LIST,
} from '../pm-section/types'
import {
  PAGE_MANAGER_SECTION_VISIBILITY_TYPE_TENURE,
  PAGE_MANAGER_SECTION_VISIBILITY_TYPE_USER_HAS_WATCHED,
  PAGE_MANAGER_SECTION_VISIBILITY_TYPE_USER_ENTITLEMENT,
  PAGE_MANAGER_SECTION_VISIBILITY_TYPE_USER_RC_TERMS,
  PAGE_MANAGER_SECTION_VISIBILITY_TYPE_USER_VIEW_COUNT,
} from '../pm-section/visibility-types'

/**
 * Common section data extractors that work for all section types
 */
const commonExtractors = {
  getAdminTitle: (section) => section.get('adminTitle', ''),
  getHeaderLabel: (section) => section.getIn(['data', 'header', 'label'], ''),
  getType: (section) => section.get('type', ''),
  getSortType: (section) => section.getIn(['data', 'sortOrder'], ''),
  getDisplayMode: (section) => section.getIn(['data', 'displayMode'], ''),
  getEnabled: (section) => (section.get('enabled', false) ? 'Y' : 'N'),
  getWebLinkLabel: (section) =>
    section.getIn(['data', 'webLink', 'label'], '') ||
    section.getIn(['data', 'viewAllLink', 'web', 'label'], ''),
  getWebLinkUrl: (section) =>
    section.getIn(['data', 'webLink', 'url'], '') ||
    section.getIn(['data', 'viewAllLink', 'web', 'url'], ''),
  getAppLinkLabel: (section) =>
    section.getIn(['data', 'appLink', 'label'], '') ||
    section.getIn(['data', 'viewAllLink', 'app', 'label'], ''),
  getAppLinkUrl: (section) =>
    section.getIn(['data', 'appLink', 'url'], '') ||
    section.getIn(['data', 'viewAllLink', 'app', 'url'], ''),
}

/**
 * Content list specific data extractors
 */
const contentListExtractors = {
  getContentListLabel: (section) => {
    const content = section.getIn(['data', 'content'], List())
    if (List.isList(content) && content.size > 0) {
      return content
        .map((item) => item.get('label', ''))
        .filter(Boolean)
        .join(',')
    }
    return section.getIn(['data', 'label'], '')
  },
  getContentList: (section, pmNodes, listIdToName) => {
    const content = section.getIn(['data', 'content'], List())
    if (List.isList(content) && content.size > 0) {
      return content
        .map((item) => {
          const listId = item.get('listId', '')
          const resolved = (listIdToName && listIdToName[String(listId)]) || listId
          return resolved
        })
        .filter(Boolean)
        .join(',')
    }
    const contentItems = section.getIn(['data', 'content'], List())
    return contentItems
      .map((item) => item.get('contentId', ''))
      .filter(Boolean)
      .join(',')
  },
}

/**
 * Content references list specific data extractors
 * Accepts a typeIdToSlug mapping for human-readable type output
 */
const contentReferencesListExtractors = {
  getContentReferenceType: (section, typeIdToSlug = {}) => {
    const typeId = section.getIn(
      ['data', 'contentReference', 'filter', 'references', 0, 'contentReferenceTypesId'],
      '',
    )
    // Use mapping if available, fallback to ID
    return typeIdToSlug[String(typeId)] || typeId || ''
  },
  getContentFilters: (section, contentIdToTitle = {}) => {
    const references = section.getIn(['data', 'contentReference', 'filter', 'references'], List())
    return references
      .map((ref) => {
        const contentType = ref.get('contentType', '')
        const contentId = ref.get('contentId', '')
        const title = contentIdToTitle[contentId] || contentId
        const output = `${title} ${contentId} ${contentType}`
        return output
      })
      .join(',')
  },
}

/**
 * Visibility rule extractors
 */
const visibilityExtractors = {
  getRCData: (section, rainbowCategoryIdToLabel = {}) => {
    const rules = section
      .get('visibility', List())
      .filter((r) => r.get('type') === PAGE_MANAGER_SECTION_VISIBILITY_TYPE_USER_RC_TERMS)

    if (rules.isEmpty()) {
      return {
        inverse: '',
        terms: '',
        rcRules: '',
      }
    }

    // NOTE: This assumes the 'inverse' flag is consistent across all rules for a section.
    // We take the value from the first rule found.
    const inverse = rules.first().get('inverse', false) ? 'Y' : 'N'

    // Map IDs to labels here!
    const allTerms = rules
      .flatMap((rule) => rule.get('terms', List()))
      .toSet()
      .toList()
    const terms = allTerms
      .map((id) => rainbowCategoryIdToLabel[id] || id)
      .filter(Boolean)
      .join(',')

    const affinityMap = {
      first: '1st affinity',
      second: '2nd affinity',
      third: '3rd affinity',
    }
    const affinityOrder = ['first', 'second', 'third']

    const allAffinities = rules
      .flatMap((rule) => rule.get('affinity', List()))
      .toSet()
      .toList()

    const rcRules = allAffinities
      .sortBy((affinity) => affinityOrder.indexOf(affinity))
      .map((value) => affinityMap[value] || value)
      .join(',')

    return {
      inverse,
      terms,
      rcRules,
    }
  },

  getTenureData: (section) => {
    const rule = section
      .get('visibility', List())
      .find((r) => r.get('type') === PAGE_MANAGER_SECTION_VISIBILITY_TYPE_TENURE)

    if (!rule) {
      return { inverse: '', ranges: '' }
    }

    const ranges = rule
      .get('tenureSelections', List())
      .map((range) => `${range.get('start', 0)}-${range.get('end', 0)}`)
      .join(',')

    return { inverse: rule.get('inverse', false) ? 'Y' : 'N', ranges: ranges || 'all' }
  },

  getHasWatchedData: (section, pmNodes) => {
    const rule = section
      .get('visibility', List())
      .find((r) => r.get('type') === PAGE_MANAGER_SECTION_VISIBILITY_TYPE_USER_HAS_WATCHED)

    if (!rule) {
      return { inverse: '', titles: '' }
    }

    const titles = rule
      .get('content', List())
      .map((item) => {
        const contentId = item.get('contentId')
        return pmNodes.getIn([contentId.toString(), 'title'], contentId)
      })
      .filter(Boolean)
      .join(',')

    return { inverse: rule.get('inverse', false) ? 'Y' : 'N', titles }
  },

  getEntitlementData: (section) => {
    const rule = section
      .get('visibility', List())
      .find((r) => r.get('type') === PAGE_MANAGER_SECTION_VISIBILITY_TYPE_USER_ENTITLEMENT)

    if (!rule) {
      return { inverse: '', entitlements: '' }
    }

    const entitlements = rule
      .get('subscriptions', List())
      .map((subId) => {
        switch (subId) {
          case CONTENT_POLICY_OPTION_SUB_AI_ONLY:
            return 'AI Only'
          case CONTENT_POLICY_OPTION_SUB_MONTHLY:
            return 'Basic Offering (monthly)'
          case CONTENT_POLICY_OPTION_SUB_LIVE_ACCESS:
            return 'All access'
          case CONTENT_POLICY_OPTION_SUB_ANNUAL:
            return 'Basic Offering (annual)'
          case CONTENT_POLICY_OPTION_ALACARTE:
            return 'Alacarte'
          default:
            return subId
        }
      })
      .filter(Boolean)
      .join(',')

    return { inverse: rule.get('inverse', false) ? 'Y' : 'N', entitlements }
  },

  getUserViewCountData: (section) => {
    const rule = section
      .get('visibility', List())
      .find((r) => r.get('type') === PAGE_MANAGER_SECTION_VISIBILITY_TYPE_USER_VIEW_COUNT)

    if (!rule) {
      return { inverse: '', threshold: '' }
    }

    const threshold = rule.get('threshold', '')
    const inverse = rule.get('inverse', false) ? 'Y' : 'N'

    return { inverse, threshold }
  },
}

/**
 * Main function to extract all section data in the required order
 * @param {Map} section – The section to extract data from
 * @param {Map} pmNodes – The pmNodes data for content titles (e.g. for "Has Watched")
 * @param {Object} typeIdToSlug – Mapping of contentReferenceTypesId to slug/type (for "Content reference type")
 * @param {Object} listIdToName – Mapping of listId (e.g. from "Content list") to human-readable name
 * @param {Object} contentIdToTitle – Mapping of contentId (e.g. from "Content filters") to human-readable title
 * @param {Object} rainbowCategoryIdToLabel – Mapping of rainbowCategoryId to human-readable label
 * @returns {Array} Array of values in the required column order (as per SECTION_CSV_HEADERS)
 */
export function extractSectionData (
  section,
  pmNodes,
  typeIdToSlug = {},
  listIdToName = {},
  contentIdToTitle = {},
  rainbowCategoryIdToLabel = {},
) {
  if (!Map.isMap(section)) {
    throw new Error('The section argument is required to be an immutable Map')
  }

  const type = commonExtractors.getType(section)
  const rcData = visibilityExtractors.getRCData(section, rainbowCategoryIdToLabel)
  const tenureData = visibilityExtractors.getTenureData(section)
  const hasWatchedData = visibilityExtractors.getHasWatchedData(section, pmNodes)
  const entitlementData = visibilityExtractors.getEntitlementData(section)
  const userViewCountData = visibilityExtractors.getUserViewCountData(section)

  // Extract type-specific data (defaults empty)
  let typeSpecificData = {
    contentListLabel: '',
    contentList: '',
    contentReferenceType: '',
    contentFilters: '',
  }

  if (type === SECTION_TYPE_CONTENT_LIST) {
    typeSpecificData = {
      contentListLabel: contentListExtractors.getContentListLabel(section),
      contentList: contentListExtractors.getContentList(section, pmNodes, listIdToName),
      contentReferenceType: '',
      contentFilters: '',
    }
  } else if (type === SECTION_TYPE_CONTENT_REFERENCES_LIST) {
    typeSpecificData = {
      contentListLabel: '',
      contentList: '',
      contentReferenceType: contentReferencesListExtractors.getContentReferenceType(
        section,
        typeIdToSlug,
      ),
      contentFilters: contentReferencesListExtractors.getContentFilters(section, contentIdToTitle),
    }
  }

  // Ensure multi-value fields (e.g. RC affinity, Tenure ranges, Entitlements) are comma-separated strings (even if empty)
  const safeJoin = (val) => (Array.isArray(val) ? val.filter(Boolean).join(',') : val || '')

  // Return array in the exact order required (as per SECTION_CSV_HEADERS)
  return [
    commonExtractors.getAdminTitle(section), // Admin title
    commonExtractors.getHeaderLabel(section), // Header label
    type, // Type
    typeSpecificData.contentListLabel, // Label (content list)
    typeSpecificData.contentList, // Content list (content list)
    typeSpecificData.contentReferenceType, // Content reference type (ref list)
    typeSpecificData.contentFilters, // Content filters (ref list)
    commonExtractors.getSortType(section), // Sort type
    commonExtractors.getWebLinkLabel(section), // Web link label
    commonExtractors.getWebLinkUrl(section), // Web link url
    commonExtractors.getAppLinkLabel(section), // App link label
    commonExtractors.getAppLinkUrl(section), // App link url
    commonExtractors.getDisplayMode(section), // Display mode
    commonExtractors.getEnabled(section), // Enable section
    rcData.inverse || '', // RC Inverse Rule (Y/N)
    rcData.rcRules, // RC Users affinity
    rcData.terms, // Targeted RC (comma-separated, human-readable)
    tenureData.inverse || '', // Tenure Rules inverse rules (Y/N)
    safeJoin(tenureData.ranges), // Tenure Rules Ranges (days) (comma-separated or "all")
    hasWatchedData.inverse || '', // Has Watched Rules – inverse rule (Y/N)
    safeJoin(hasWatchedData.titles), // Has Watched Title (comma-separated)
    entitlementData.inverse || '', // Entitlement Rules – inverse rule (Y/N)
    safeJoin(entitlementData.entitlements), // Entitlements (comma-separated)
    userViewCountData.inverse || '', // User View Count Rules – inverse rule (Y/N)
    userViewCountData.threshold || '', // User View Count Threshold
  ]
}
